// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { MainWindowManager } from '../../UtilsClasses/MainWindowManager';
import { InsDefItem, InsDefSettings } from '@shared/commons/cache/InstrumentDefaults';
import InstrumentsDefaultsLookupTemplate from '@front/templates/InstrumentsDefaultsLookupTemplate.html';
import { ContainerControl } from '../ContainerControl';
import { TerceraInstrumentLookupDropDownForm } from './TerceraInstrumentLookupDropDownForm';
import { LookupDropDownShowParams } from '../../UtilsClasses/LookupDropDownShowParams';
import { InstrumentLookupManager } from './InstrumentLookupManager';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { Instrument } from '@shared/commons/cache/Instrument';
import { DataCache } from '@shared/commons/DataCache';
import { TerceraSymbolLookupBaseDataProvider } from '@shared/commons/NoNFixedListCore';
import { InstrumentLookupProperties, InstrumentLookupType } from './InstrumentLookupManagerProps';
import { WDSettingsUtils } from '../../UtilsClasses/WDGeneralSettingsUtils';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { InstrumentLookupQuickTreeNode } from '../QuickTree/InstrumentLookupQuickTreeNode';
export class InstrumentsDefaultsLookup extends ContainerControl {
    constructor() {
        super(...arguments);
        this.instruments = [];
        this.selectedInstrument = null;
        this.searchDebounceMs = 300;
        this.lastTimeoutHandlerId = null;
        this.instrTypesToIgnore = [InstrumentTypes.INDICIES];
        this.onSearchDebounced = () => {
            this.lastTimeoutHandlerId = null;
            this.timeoutCallBack();
        };
        this.getSelectedInstruments = (node) => {
            if (isNullOrUndefined(node)) {
                return [];
            }
            const instruments = [];
            if (isValidArray(node.childNodes)) {
                for (const childNode of node.childNodes) {
                    instruments.push(...this.getSelectedInstruments(childNode));
                }
            }
            else {
                const tag = node.tag;
                if (tag instanceof Instrument) {
                    instruments.push(tag);
                }
            }
            return instruments;
        };
        this.removeSelectedInstruments = (instruments) => {
            if (!isValidArray(instruments)) {
                return;
            }
            this.instruments = this.instruments.filter((instrument) => !instruments.includes(instrument));
            const all = this.get('allSettings');
            if (!isNullOrUndefined(all)) {
                all.RemoveInstrumentSettings(instruments);
            }
            this.set('itemSettings', InstrumentsDefaultsLookup.createInsDefItemNullStub());
            this.Repopulate(true);
        };
        this.instrumentsDropDownCallback = (instruments) => {
            this.AddInstruments(instruments);
        };
    }
    oninit() {
        super.oninit();
        this.dataProvider = new TerceraSymbolLookupBaseDataProvider();
        void this.set('itemSettings', InstrumentsDefaultsLookup.createInsDefItemNullStub());
        this.observe('searchText', this.onSearchTextChanged);
        this.observe('allSettings', this.onAllSettingsChanged);
        this.on('instrumentSelection', this.onInstrumentSelection);
        this.on('openInstrumentLookup', this.openInstrumentLookup);
        this.on('quickTreeAfterMouseDown', this.quickTreeAfterMouseDown);
    }
    oncomplete() {
        super.oncomplete();
        const qtr = this.Controls.quickTreeRactive;
        if (!isNullOrUndefined(qtr === null || qtr === void 0 ? void 0 : qtr.quickTree)) {
            qtr.quickTree.isSmall = true;
            qtr.quickTree.rowHeigth = InstrumentLookupQuickTreeNode.ROW_HEIGHT;
            qtr.quickTree.nodeOffset = 12;
        }
    }
    dispose() {
        if (this.lastTimeoutHandlerId !== null) {
            clearTimeout(this.lastTimeoutHandlerId);
            this.lastTimeoutHandlerId = null;
        }
        super.dispose();
    }
    quickTreeAfterMouseDown(event) {
        const e = event.original;
        if (e.button !== 2)
            return;
        const qt = this.Controls.quickTreeRactive.quickTree;
        const selectedNode = qt.selectedNode;
        if (isNullOrUndefined(selectedNode))
            return;
        const instrument = selectedNode.tag;
        if (!(instrument instanceof Instrument)) {
            return;
        }
        const items = [];
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.menu.Remove',
            value: selectedNode,
            event: (item) => {
                const instruments = this.getSelectedInstruments(item.value);
                this.removeSelectedInstruments(instruments);
            }
        });
        showContextMenu({ x: e.clientX, y: e.clientY, items: items });
    }
    onInstrumentSelection() {
        var _a;
        const node = this.Controls.quickTreeRactive.quickTree.selectedNode;
        if (isNullOrUndefined(node))
            return;
        this.updateNumericFromNodeOrParents(node);
        const stub = InstrumentsDefaultsLookup.createInsDefItemNullStub();
        const tag = node.tag;
        if (isNullOrUndefined(tag)) {
            this.set('itemSettings', stub);
            return;
        }
        const hasOwnSettings = (tag instanceof Instrument || this.tryGetTypeStrFromTag(tag) !== null) &&
            !this.isOptionSeriesNode(node);
        const itemSettings = hasOwnSettings
            ? (_a = this.tryGetSettingsFromTag(tag)) !== null && _a !== void 0 ? _a : stub
            : this.getReadonlySettingsFromParents(node);
        this.set('itemSettings', itemSettings);
    }
    UpdateNumeric(type) {
        let step = 1;
        let minVal = 1;
        let decPrec = 0;
        if (type === InstrumentTypes.FOREX && WDSettingsUtils.IsTicksFractionalForForex()) {
            step = 0.1;
            minVal = 0.1;
            decPrec = 1;
        }
        if (!isNullOrUndefined(MainWindowManager.TypesManagerScreen)) {
            MainWindowManager.TypesManagerScreen.set({
                sltpStep: step,
                sltpMinValue: minVal,
                sltpDecimalPrecision: decPrec
            });
        }
    }
    onAllSettingsChanged(newSettings) {
        if (isNullOrUndefined(newSettings)) {
            return;
        }
        this.instruments.length = 0;
        void this.set('searchText', '');
        this.AddInstrumentsPromise(newSettings.InstrumentsNames);
    }
    onSearchTextChanged() {
        if (this.lastTimeoutHandlerId !== null) {
            clearTimeout(this.lastTimeoutHandlerId);
        }
        this.lastTimeoutHandlerId = setTimeout(this.onSearchDebounced, this.searchDebounceMs);
    }
    timeoutCallBack() {
        this.Repopulate();
    }
    openInstrumentLookup() {
        const instruments = this.getInstrumentsForLookup();
        const obj = { items: instruments };
        const params = new LookupDropDownShowParams();
        params.items = obj.items;
        params.callBack = this.instrumentsDropDownCallback;
        params.isMultiSelect = true;
        params.isCentered = true;
        params.autoClose = false;
        params.parentPanel = this;
        params.dataProvider = this.dataProvider;
        params.instrTypesToIgnore = this.instrTypesToIgnore;
        TerceraInstrumentLookupDropDownForm.ShowForm(params);
    }
    AddInstrumentsPromise(InstrumentsNames) {
        void Promise.all(InstrumentsNames.map((name) => this.dataProvider.getInstrumentByName(name))).then((instruments) => this.AddInstruments(instruments));
    }
    AddInstruments(instruments) {
        for (const instrument of instruments) {
            this.addInstrumentInternal(instrument);
        }
        this.Repopulate();
    }
    AddInstrument(instrument) {
        this.selectedInstrument = instrument;
        this.AddInstruments([instrument]);
    }
    Repopulate(saveScrollIndex) {
        const qtr = this.Controls.quickTreeRactive;
        const quickTree = this.Controls.quickTreeRactive.quickTree;
        const properties = new InstrumentLookupProperties(InstrumentLookupType.Big, this.instruments, quickTree, this.get('searchText'));
        properties.isInstrumentDefaults = true;
        properties.selectedInstruments = this.selectedInstrument ? this.selectedInstrument : this.instruments.slice(-1);
        properties.existingInstrumentTypes = DataCache.ExistingInstrumentTypes;
        const scrollIndex = qtr.quickTree.scroll.scrollIndex;
        InstrumentLookupManager.fillTree(properties);
        qtr.setSizes();
        if (saveScrollIndex) {
            const newScrollIndex = quickTree.scroll.correctScrollIndex(Math.max(0, scrollIndex - 1));
            quickTree.scroll.moveScrollToElement(newScrollIndex);
        }
        // TEST.
        qtr.quickTree.Draw(true);
    }
    themeChange() {
        const addBtn = this.Controls.add;
        if (!isNullOrUndefined(addBtn)) {
            addBtn.set('terceraButtonStyle', 'image-button-plain');
        }
    }
    // HACK. Ractive doesn't like keypaths like ".itemSettings.SLDefaultOffsetTicksDecimal"
    // when itemSettings is null and two-way binding is used.
    static createInsDefItemNullStub() {
        const insDefItem = new InsDefItem();
        insDefItem.readOnly = true;
        return insDefItem;
    }
    getInstrumentsForLookup() {
        const allInstruments = DataCache.Instruments;
        const keys = Object.keys(allInstruments);
        const instrumentsForLookup = [];
        const len = keys.length;
        for (let i = 0; i < len; i++) {
            const instrument = allInstruments[keys[i]];
            if (this.instrTypesToIgnore.includes(instrument.InstrType)) {
                continue;
            }
            instrumentsForLookup.push(instrument);
        }
        return instrumentsForLookup;
    }
    addInstrumentInternal(instrument) {
        if (isNullOrUndefined(instrument)) {
            return;
        }
        if (!this.instruments.includes(instrument)) {
            this.instruments.push(instrument);
            const all = this.get('allSettings');
            if (!isNullOrUndefined(all)) {
                all.CreateInstrumentSettingsIfNotExist(instrument);
            }
        }
    }
    tryGetTypeStrFromTag(tag) {
        if (typeof tag !== 'string')
            return null;
        const prefix = InsDefSettings.INSTRUMENT_TYPE;
        return tag.startsWith(prefix) ? tag.slice(prefix.length) : null;
    }
    tryGetTypeFromTag(tag) {
        if (tag instanceof Instrument) {
            return tag.InstrType;
        }
        const typeStr = this.tryGetTypeStrFromTag(tag);
        if (typeStr === null) {
            return null;
        }
        const t = Number.parseInt(typeStr, 10);
        return Number.isFinite(t) ? t : null;
    }
    tryGetSettingsFromTag(tag) {
        var _a, _b;
        const all = this.get('allSettings');
        if (isNullOrUndefined(all))
            return null;
        if (tag instanceof Instrument) {
            return (_a = all.GetInstrumentSettings(tag)) !== null && _a !== void 0 ? _a : null;
        }
        const typeStr = this.tryGetTypeStrFromTag(tag);
        if (typeStr !== null) {
            return (_b = all.GetTypeSettings(typeStr)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    static cloneInsDefItemForView(src) {
        const dst = new InsDefItem();
        Object.assign(dst, src);
        dst.DefaultLots = null;
        dst.readOnly = true;
        return dst;
    }
    updateNumericFromNodeOrParents(node) {
        for (let cur = node; !isNullOrUndefined(cur); cur = cur.parentNode) {
            const t = this.tryGetTypeFromTag(cur.tag);
            if (t !== null) {
                this.UpdateNumeric(t);
                return;
            }
        }
    }
    getReadonlySettingsFromParents(node) {
        for (let cur = node === null || node === void 0 ? void 0 : node.parentNode; !isNullOrUndefined(cur); cur = cur.parentNode) {
            const s = this.tryGetSettingsFromTag(cur.tag);
            if (s)
                return InstrumentsDefaultsLookup.cloneInsDefItemForView(s);
        }
        return InstrumentsDefaultsLookup.createInsDefItemNullStub();
    }
    isOptionSeriesNode(node) {
        return (node === null || node === void 0 ? void 0 : node.missAtUnCollapse) === true;
    }
}
ContainerControl.extendWith(InstrumentsDefaultsLookup, {
    data: function () {
        return {
            allSettings: null,
            itemSettings: null,
            searchText: ''
        };
    },
    template: InstrumentsDefaultsLookupTemplate
});
